/**
 * Service comptable centralisé pour OMAD SERVICES
 * 
 * Ce service gère :
 * - La génération et suivi des devis
 * - La création et gestion des factures
 * - Le suivi des paiements
 * - Les rapports financiers
 * - La conversion automatique prospect → client
 */

import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { genererDevis, formaterEuro } from './tarificationService';
import { convertirProspectEnClient } from './clientStatusService';

// Statuts des documents
const STATUTS = {
  DEVIS: {
    BROUILLON: 'brouillon',
    ENVOYE: 'envoye',
    ACCEPTE: 'accepte',
    REFUSE: 'refuse',
    EXPIRE: 'expire'
  },
  FACTURE: {
    EN_ATTENTE: 'en_attente',
    PAYEE: 'payee',
    EN_RETARD: 'en_retard',
    ANNULEE: 'annulee'
  },
  PROSPECT: {
    CONTACT: 'contact',
    DEVIS_ENVOYE: 'devis_envoye',
    NEGOCIATION: 'negociation',
    SIGNE: 'signe',
    PERDU: 'perdu'
  }
};

// Types de documents
const TYPES_DOCUMENT = {
  DEVIS: 'devis',
  FACTURE: 'facture',
  AVOIR: 'avoir'
};

/**
 * Génère un numéro de document séquentiel
 * @param {string} prefixe - Préfixe du numéro (DEV, FAC, etc.)
 * @param {number} dernierNumero - Dernier numéro utilisé
 * @returns {string} Numéro complet
 */
export const genererNumeroDocument = (prefixe, dernierNumero = 0) => {
  const numero = (dernierNumero + 1).toString().padStart(4, '0');
  const annee = new Date().getFullYear();
  return `${prefixe}-${annee}-${numero}`;
};

/**
 * Nettoie un objet en remplaçant les valeurs undefined par des valeurs par défaut
 * @param {Object} obj - Objet à nettoyer
 * @returns {Object} Objet nettoyé
 */
const nettoyerObjetPourFirestore = (obj) => {
  if (obj === null || obj === undefined) return null;
  
  // Ne pas nettoyer les Timestamps Firebase
  if (obj && typeof obj === 'object' && 'toDate' in obj && typeof obj.toDate === 'function') {
    return obj; // Conserver les Timestamps intacts
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => nettoyerObjetPourFirestore(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) {
        // Remplacer undefined par null pour Firestore
        cleaned[key] = null;
      } else if (value === null) {
        cleaned[key] = null;
      } else if (Array.isArray(value)) {
        cleaned[key] = value.map(item => nettoyerObjetPourFirestore(item));
      } else if (typeof value === 'object') {
        // Vérifier si c'est un Timestamp Firebase
        if (value && 'toDate' in value && typeof value.toDate === 'function') {
          cleaned[key] = value; // Conserver les Timestamps intacts
        } else {
          cleaned[key] = nettoyerObjetPourFirestore(value);
        }
      } else {
        cleaned[key] = value;
      }
    }
    return cleaned;
  }
  
  return obj;
};

/**
 * Crée un nouveau devis
 * @param {Object} db - Instance Firestore
 * @param {Object} contrat - Contrat client
 * @param {Array} lignesPrestations - Lignes de prestations
 * @param {Object} client - Informations client
 * @param {Object} options - Options supplémentaires
 * @returns {Promise} Devis créé
 */
export const creerDevis = async (db, contrat, lignesPrestations, client, options = {}) => {
  try {
    // Générer le devis avec le service de tarification et les collaborateurs sélectionnés
    // Extraire les collaborateurs des lignes de prestations
    const collaborateursIds = [...new Set(lignesPrestations.map(l => l.employeeId).filter(Boolean))];
    
    // Charger les détails des collaborateurs depuis la base de données
    let collaborateurs = [];
    if (collaborateursIds.length > 0) {
      const employeesQuery = query(collection(db, 'employees'), where('__name__', 'in', collaborateursIds));
      const employeesSnapshot = await getDocs(employeesQuery);
      collaborateurs = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    const devisCalculé = genererDevis(contrat, lignesPrestations, client, collaborateurs);
    
    // Récupérer le dernier numéro de devis
    const devisQuery = query(
      collection(db, 'devis'),
      orderBy('numero', 'desc'),
      where('numero', '>=', `DEV-${new Date().getFullYear()}-`)
    );
    const devisSnapshot = await getDocs(devisQuery);
    const dernierDevis = devisSnapshot.docs[0]?.data();
    const dernierNumero = dernierDevis ? parseInt(dernierDevis.numero.split('-')[2]) : 0;
    
    // Créer le document devis
    const devisData = {
      numero: genererNumeroDocument('DEV', dernierNumero),
      clientId: client.id,
      clientNom: `${client.firstName} ${client.lastName}`,
      clientEmail: client.email || '',
      clientTelephone: client.phone || client.phoneNumber || '',
      clientAdresse: client.address || '',
      contratId: contrat.id || 'contrat-par-defaut',
      contratParams: contrat, // SAUVEGARDER les paramètres du contrat
      lignesPrestations: lignesPrestations,
      calculs: devisCalculé,
      montantHT: devisCalculé.totaux.totalCesu,
      montantTTC: devisCalculé.totaux.totalTTC,
      statut: STATUTS.DEVIS.BROUILLON,
      dateCreation: Timestamp.now(),
      dateValidite: options.dateValidite || Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 jours par défaut
      dateDebut: options.dateDebut || null,
      dateFin: options.dateFin || null,
      createdBy: options.userId,
      notes: options.notes || '',
      conditionsPaiement: options.conditionsPaiement || 'Paiement à 30 jours fin de mois'
    };
    
    // Nettoyer l'objet pour Firestore
    const devisDataNettoye = nettoyerObjetPourFirestore(devisData);
    
    const docRef = await addDoc(collection(db, 'devis'), devisDataNettoye);
    return { id: docRef.id, ...devisDataNettoye };
    
  } catch (error) {
    console.error('Erreur création devis:', error);
    throw error;
  }
};

/**
 * Crée une facture à partir d'un devis accepté
 * @param {Object} db - Instance Firestore
 * @param {string} devisId - ID du devis accepté
 * @param {Object} options - Options supplémentaires
 * @returns {Promise} Facture créée
 */
export const creerFacture = async (db, devisId, options = {}) => {
  try {
    // Récupérer le devis
    const devisDoc = await getDocs(query(collection(db, 'devis'), where('id', '==', devisId)));
    if (devisDoc.empty) {
      throw new Error('Devis non trouvé');
    }
    
    const devis = devisDoc.docs[0].data();
    
    if (devis.statut !== STATUTS.DEVIS.ACCEPTE) {
      throw new Error('Le devis doit être accepté pour créer une facture');
    }
    
    // Récupérer le dernier numéro de facture
    const facturesQuery = query(
      collection(db, 'factures'),
      orderBy('numero', 'desc'),
      where('numero', '>=', `FAC-${new Date().getFullYear()}-`)
    );
    const facturesSnapshot = await getDocs(facturesQuery);
    const derniereFacture = facturesSnapshot.docs[0]?.data();
    const dernierNumero = derniereFacture ? parseInt(derniereFacture.numero.split('-')[2]) : 0;
    
    // Calculer la date d'échéance
    const dateEmission = Timestamp.now();
    const dateEcheance = Timestamp.fromDate(
      new Date(dateEmission.toDate().getTime() + (options.delaiPaiement || 30) * 24 * 60 * 60 * 1000)
    );
    
    // Créer la facture
    const factureData = {
      numero: genererNumeroDocument('FAC', dernierNumero),
      devisId: devisId,
      clientId: devis.clientId,
      clientNom: devis.clientNom,
      clientEmail: devis.clientEmail,
      clientTelephone: devis.clientTelephone,
      clientAdresse: devis.clientAdresse,
      montantHT: devis.montantHT,
      montantTTC: devis.montantTTC,
      tva: devis.montantTTC - devis.montantHT,
      statut: STATUTS.FACTURE.EN_ATTENTE,
      dateEmission: dateEmission,
      dateEcheance: dateEcheance,
      delaiPaiement: options.delaiPaiement || 30,
      modePaiement: options.modePaiement || 'Virement',
      createdBy: options.userId,
      notes: options.notes || '',
      historiquePaiements: []
    };
    
    const docRef = await addDoc(collection(db, 'factures'), factureData);
    
    // Marquer le devis comme facturé
    await updateDoc(doc(db, 'devis', devisId), {
      factureId: docRef.id,
      dateFacturation: Timestamp.now()
    });
    
    return { id: docRef.id, ...factureData };
    
  } catch (error) {
    console.error('Erreur création facture:', error);
    throw error;
  }
};

/**
 * Enregistre un paiement
 * @param {Object} db - Instance Firestore
 * @param {string} factureId - ID de la facture
 * @param {Object} paiement - Données du paiement
 * @returns {Promise} Paiement enregistré
 */
export const enregistrerPaiement = async (db, factureId, paiement) => {
  try {
    const factureRef = doc(db, 'factures', factureId);
    const factureDoc = await getDocs(query(collection(db, 'factures'), where('id', '==', factureId)));
    
    if (factureDoc.empty) {
      throw new Error('Facture non trouvée');
    }
    
    const facture = factureDoc.docs[0].data();
    const historiquePaiements = facture.historiquePaiements || [];
    
    const nouveauPaiement = {
      id: crypto.randomUUID(),
      montant: paiement.montant,
      date: Timestamp.now(),
      mode: paiement.mode || 'Virement',
      reference: paiement.reference || '',
      notes: paiement.notes || ''
    };
    
    historiquePaiements.push(nouveauPaiement);
    
    // Calculer le total payé
    const totalPaye = historiquePaiements.reduce((sum, p) => sum + p.montant, 0);
    const nouveauStatut = totalPaye >= facture.montantTTC ? STATUTS.FACTURE.PAYEE : STATUTS.FACTURE.EN_ATTENTE;
    
    await updateDoc(factureRef, {
      historiquePaiements: historiquePaiements,
      totalPaye: totalPaye,
      statut: nouveauStatut,
      dateDernierPaiement: Timestamp.now()
    });
    
    return nouveauPaiement;
    
  } catch (error) {
    console.error('Erreur enregistrement paiement:', error);
    throw error;
  }
};

/**
 * Récupère les statistiques financières
 * @param {Object} db - Instance Firestore
 * @param {Object} periode - Période à analyser
 * @returns {Promise} Statistiques
 */
export const getStatistiquesFinancieres = async (db, periode = {}) => {
  try {
    const { debut = new Date(new Date().getFullYear(), 0, 1), fin = new Date() } = periode;
    
    const debutTimestamp = Timestamp.fromDate(new Date(debut));
    const finTimestamp = Timestamp.fromDate(new Date(fin));
    
    // Récupérer les factures de la période
    const facturesQuery = query(
      collection(db, 'factures'),
      where('dateEmission', '>=', debutTimestamp),
      where('dateEmission', '<=', finTimestamp)
    );
    
    const facturesSnapshot = await getDocs(facturesQuery);
    const factures = facturesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Calculer les statistiques
    const stats = {
      periode: { debut, fin },
      totalFactures: factures.length,
      chiffreAffaires: factures.reduce((sum, f) => sum + f.montantTTC, 0),
      facturesPayees: factures.filter(f => f.statut === STATUTS.FACTURE.PAYEE).length,
      facturesEnAttente: factures.filter(f => f.statut === STATUTS.FACTURE.EN_ATTENTE).length,
      facturesEnRetard: factures.filter(f => f.statut === STATUTS.FACTURE.EN_RETARD).length,
      totalEnRetard: factures
        .filter(f => f.statut === STATUTS.FACTURE.EN_RETARD)
        .reduce((sum, f) => sum + f.montantTTC, 0),
      moyenneFacture: factures.length > 0 ? factures.reduce((sum, f) => sum + f.montantTTC, 0) / factures.length : 0
    };
    
    return stats;
    
  } catch (error) {
    console.error('Erreur récupération statistiques:', error);
    throw error;
  }
};

/**
 * Accepte un devis et convertit automatiquement le prospect en client
 * @param {Object} db - Instance Firestore
 * @param {string} devisId - ID du devis à accepter
 * @param {Object} options - Options supplémentaires
 * @returns {Promise<Object>} Résultat de l'acceptation
 */
export const accepterDevis = async (db, devisId, options = {}) => {
  try {
    console.log(`📄 Acceptation du devis: ${devisId}`);
    
    // Récupérer le devis
    const devisDoc = await getDocs(query(collection(db, 'devis'), where('__name__', '==', devisId)));
    if (devisDoc.empty) {
      throw new Error('Devis non trouvé');
    }
    
    const devis = { id: devisDoc.docs[0].id, ...devisDoc.docs[0].data() };
    
    if (devis.statut === STATUTS.DEVIS.ACCEPTE) {
      console.log(`⚠️ Devis ${devisId} déjà accepté`);
      return { devis, conversionEffectuee: false };
    }
    
    // Mettre à jour le statut du devis
    const devisRef = doc(db, 'devis', devisId);
    await updateDoc(devisRef, {
      statut: STATUTS.DEVIS.ACCEPTE,
      dateAcceptation: Timestamp.now(),
      acceptePar: options.acceptePar || 'système',
      notesAcceptation: options.notes || ''
    });
    
    console.log(`✅ Devis ${devisId} marqué comme accepté`);
    
    // Convertir automatiquement le prospect en client actif
    let conversionEffectuee = false;
    try {
      await convertirProspectEnClient(db, devis.clientId, devisId);
      conversionEffectuee = true;
      console.log(`🎉 Conversion prospect → client effectuée pour: ${devis.clientNom}`);
    } catch (conversionError) {
      console.warn('Erreur lors de la conversion prospect → client:', conversionError);
      // Ne pas faire échouer l'acceptation du devis si la conversion échoue
    }
    
    return {
      devis: { ...devis, statut: STATUTS.DEVIS.ACCEPTE, dateAcceptation: new Date() },
      conversionEffectuee,
      clientId: devis.clientId,
      message: `Devis ${devis.numero} accepté${conversionEffectuee ? ' - Client converti' : ''}`
    };
    
  } catch (error) {
    console.error('Erreur lors de l\'acceptation du devis:', error);
    throw error;
  }
};

/**
 * Refuse un devis avec raison
 * @param {Object} db - Instance Firestore
 * @param {string} devisId - ID du devis à refuser
 * @param {Object} options - Options (raison, notes)
 * @returns {Promise<Object>} Résultat du refus
 */
export const refuserDevis = async (db, devisId, options = {}) => {
  try {
    console.log(`❌ Refus du devis: ${devisId}`);
    
    const devisRef = doc(db, 'devis', devisId);
    await updateDoc(devisRef, {
      statut: STATUTS.DEVIS.REFUSE,
      dateRefus: Timestamp.now(),
      raisonRefus: options.raison || 'Non spécifiée',
      notesRefus: options.notes || ''
    });
    
    console.log(`✅ Devis ${devisId} marqué comme refusé`);
    
    return {
      devisId,
      statut: STATUTS.DEVIS.REFUSE,
      message: 'Devis refusé'
    };
    
  } catch (error) {
    console.error('Erreur lors du refus du devis:', error);
    throw error;
  }
};

/**
 * Vérifie les factures en retard
 * @param {Object} db - Instance Firestore
 * @returns {Promise} Liste des factures en retard
 */
export const verifierFacturesEnRetard = async (db) => {
  try {
    const aujourdhui = Timestamp.now();
    
    const facturesQuery = query(
      collection(db, 'factures'),
      where('statut', '==', STATUTS.FACTURE.EN_ATTENTE),
      where('dateEcheance', '<', aujourdhui)
    );
    
    const facturesSnapshot = await getDocs(facturesQuery);
    const facturesEnRetard = facturesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Mettre à jour le statut des factures en retard
    await Promise.all(
      facturesEnRetard.map(async (facture) => {
        const factureRef = doc(db, 'factures', facture.id);
        await updateDoc(factureRef, { statut: STATUTS.FACTURE.EN_RETARD });
      })
    );
    
    return facturesEnRetard;
    
  } catch (error) {
    console.error('Erreur vérification factures en retard:', error);
    throw error;
  }
};

/**
 * Exporte les données comptables
 * @param {Object} db - Instance Firestore
 * @param {string} type - Type d'export (factures, devis, transactions)
 * @param {Object} periode - Période d'export
 * @returns {Promise} Données formatées
 */
export const exporterDonneesComptables = async (db, type = 'factures', periode = {}) => {
  try {
    const { debut = new Date(new Date().getFullYear(), 0, 1), fin = new Date() } = periode;
    const debutTimestamp = Timestamp.fromDate(new Date(debut));
    const finTimestamp = Timestamp.fromDate(new Date(fin));
    
    let collectionName, queryConditions;
    
    switch (type) {
      case 'factures':
        collectionName = 'factures';
        queryConditions = [
          where('dateEmission', '>=', debutTimestamp),
          where('dateEmission', '<=', finTimestamp)
        ];
        break;
      case 'devis':
        collectionName = 'devis';
        queryConditions = [
          where('dateCreation', '>=', debutTimestamp),
          where('dateCreation', '<=', finTimestamp)
        ];
        break;
      default:
        throw new Error('Type d\'export non supporté');
    }
    
    const exportQuery = query(collection(db, collectionName), ...queryConditions, orderBy('dateCreation', 'asc'));
    const snapshot = await getDocs(exportQuery);
    
    const donnees = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convertir les timestamps en dates lisibles
        dateCreation: data.dateCreation?.toDate().toISOString().split('T')[0],
        dateEmission: data.dateEmission?.toDate().toISOString().split('T')[0],
        dateEcheance: data.dateEcheance?.toDate().toISOString().split('T')[0]
      };
    });
    
    return {
      type,
      periode: { debut, fin },
      nombreDocuments: donnees.length,
      donnees
    };
    
  } catch (error) {
    console.error('Erreur export données comptables:', error);
    throw error;
  }
};

// Export des constantes
export { STATUTS, TYPES_DOCUMENT };
